/* Jamila presentation behavior. Commerce remains handled by Kalles. */
(() => {
  const AUTOPLAY_DELAY = 5000;
  const SWIPE_THRESHOLD = 45;

  if (!customElements.get('jamila-slider')) {
    customElements.define('jamila-slider', class extends HTMLElement {
      connectedCallback() {
        this.stopAutoplay();
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };

        this.slides = [...this.querySelectorAll('[data-jamila-slide]')];
        this.dots = [...this.querySelectorAll('[data-jamila-dot]')];
        this.status = this.querySelector('[data-jamila-slide-status]');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.index = Math.max(0, this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false'));
        this.pointerStart = null;
        this.swipedRecently = false;
        this.hovered = this.matches(':hover');
        this.focused = this.contains(document.activeElement);
        this.editorSelected = false;

        this.querySelector('[data-jamila-prev]')?.addEventListener('click', () => this.select(this.index - 1), options);
        this.querySelector('[data-jamila-next]')?.addEventListener('click', () => this.select(this.index + 1), options);
        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.select(index), options));

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.select(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }, options);
        this.addEventListener('dragstart', (event) => event.preventDefault(), options);

        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          this.hovered = true;
          this.stopAutoplay();
        }, options);
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'touch') return;
          this.hovered = false;
          this.startAutoplay();
        }, options);
        this.addEventListener('focusin', () => {
          this.focused = true;
          this.stopAutoplay();
        }, options);
        this.addEventListener('focusout', (event) => {
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          this.focused = false;
          this.startAutoplay();
        }, options);

        this.addEventListener('pointerdown', (event) => {
          const slide = event.target.closest('[data-jamila-slide]');
          if (!event.isPrimary || !slide) return;
          this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
          this.stopAutoplay();
          slide.setPointerCapture?.(event.pointerId);
        }, options);
        this.addEventListener('pointerup', (event) => {
          if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
          const deltaX = event.clientX - this.pointerStart.x;
          const deltaY = event.clientY - this.pointerStart.y;
          this.pointerStart = null;
          if (Math.abs(deltaX) >= SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
            this.swipedRecently = true;
            this.show(this.index + (deltaX < 0 ? 1 : -1), true);
          }
          this.startAutoplay();
        }, options);
        this.addEventListener('pointercancel', () => {
          this.pointerStart = null;
          this.startAutoplay();
        }, options);
        this.addEventListener('click', (event) => {
          if (!this.swipedRecently || !event.target.closest('[data-jamila-slide]')) return;
          event.preventDefault();
          this.swipedRecently = false;
        }, { capture: true, signal: this.controller.signal });

        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.stopAutoplay();
          else this.startAutoplay();
        }, options);

        this.reducedMotionChange = () => {
          if (this.reduceMotion.matches) this.stopAutoplay();
          else this.startAutoplay();
        };
        this.reduceMotion.addEventListener?.('change', this.reducedMotionChange);

        this.addEventListener('shopify:block:select', (event) => {
          const index = this.slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
          this.editorSelected = true;
          this.stopAutoplay();
          if (index >= 0) this.show(index, true);
        }, options);
        this.addEventListener('shopify:block:deselect', () => {
          this.editorSelected = false;
          this.startAutoplay();
        }, options);

        this.show(this.index, false);
        this.startAutoplay();
      }

      select(index) {
        this.show(index, true);
        this.startAutoplay();
      }

      show(index, announce = true) {
        if (!this.slides.length) return;
        this.index = (index + this.slides.length) % this.slides.length;
        this.hydrateSlide(this.slides[this.index]);
        this.slides.forEach((slide, slideIndex) => {
          const active = slideIndex === this.index;
          slide.setAttribute('aria-hidden', String(!active));
          slide.tabIndex = active ? 0 : -1;
        });
        this.dots.forEach((dot, dotIndex) => {
          const active = dotIndex === this.index;
          dot.setAttribute('aria-pressed', String(active));
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        if (announce && this.status) this.status.textContent = `Slide ${this.index + 1} of ${this.slides.length}`;
      }

      hydrateSlide(slide) {
        if (!slide) return;
        slide.querySelectorAll('[data-jamila-slide-source]').forEach((source) => {
          if (source.dataset.srcset) source.srcset = source.dataset.srcset;
          source.removeAttribute('data-jamila-slide-source');
        });
        const image = slide.querySelector('[data-jamila-slide-image]');
        if (!image) return;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
        image.removeAttribute('data-jamila-slide-image');
      }

      startAutoplay() {
        if (
          this.reduceMotion?.matches ||
          this.slides.length < 2 ||
          document.hidden ||
          this.hovered ||
          this.focused ||
          this.pointerStart ||
          this.editorSelected
        ) return;
        this.stopAutoplay();
        this.timer = window.setInterval(() => this.show(this.index + 1, false), AUTOPLAY_DELAY);
      }

      stopAutoplay() {
        window.clearInterval(this.timer);
        this.timer = undefined;
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.controller?.abort();
        this.reduceMotion?.removeEventListener?.('change', this.reducedMotionChange);
      }
    });
  }

  if (!customElements.get('jamila-brand-loop')) {
    customElements.define('jamila-brand-loop', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        const pause = () => this.classList.add('is-jamila-paused');
        const resume = () => this.classList.remove('is-jamila-paused');
        this.addEventListener('pointerdown', pause, options);
        this.addEventListener('pointerup', resume, options);
        this.addEventListener('pointercancel', resume, options);
        this.addEventListener('pointerleave', resume, options);
      }
      disconnectedCallback() { this.controller?.abort(); }
    });
  }

  if (!customElements.get('jamila-review-slider')) {
    customElements.define('jamila-review-slider', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };

        this.slides = [...this.querySelectorAll('[data-jamila-review]')];
        this.dots = [...this.querySelectorAll('[data-jamila-review-dot]')];
        this.viewport = this.querySelector('[data-jamila-review-viewport]');
        this.track = this.querySelector('[data-jamila-review-track]');
        this.status = this.querySelector('[data-jamila-review-status]');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.index = Number.parseInt(this.dataset.jamilaInitial || '0', 10);
        this.pointerStart = null;

        this.querySelector('[data-jamila-review-prev]')?.addEventListener('click', () => this.show(this.index - 1, true), options);
        this.querySelector('[data-jamila-review-next]')?.addEventListener('click', () => this.show(this.index + 1, true), options);
        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true), options));

        this.addEventListener('keydown', (event) => {
          if (event.target.closest('[data-jamila-review-source]')) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }, options);
        this.addEventListener('dragstart', (event) => event.preventDefault(), options);
        this.addEventListener('selectstart', (event) => {
          if (this.pointerStart) event.preventDefault();
        }, options);
        this.addEventListener('pointerdown', (event) => {
          if (!event.isPrimary ||
              event.target.closest('[data-jamila-review-prev]') ||
              event.target.closest('[data-jamila-review-next]') ||
              event.target.closest('[data-jamila-review-dot]') ||
              event.target.closest('[data-jamila-review-source]') ||
              !event.target.closest('[data-jamila-review]')) return;
          this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
          this.classList.add('is-dragging');
          this.viewport?.setPointerCapture?.(event.pointerId);
        }, options);
        this.addEventListener('pointerup', (event) => {
          if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
          const deltaX = event.clientX - this.pointerStart.x;
          const deltaY = event.clientY - this.pointerStart.y;
          this.pointerStart = null;
          this.classList.remove('is-dragging');
          if (Math.abs(deltaX) >= SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
            this.show(this.index + (deltaX < 0 ? 1 : -1), true);
          }
        }, options);
        this.addEventListener('pointercancel', () => {
          this.pointerStart = null;
          this.classList.remove('is-dragging');
        }, options);
        this.addEventListener('shopify:block:select', (event) => {
          const index = this.slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
          if (index >= 0) this.show(index, true);
        }, options);

        this.onResize = () => this.positionCards();
        window.addEventListener?.('resize', this.onResize, options);
        if ('ResizeObserver' in window) {
          this.resizeObserver = new window.ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.viewport);
        }

        this.show(this.index, false);
        this.classList.add('is-ready');
      }

      getRelativeIndex(slideIndex) {
        let distance = slideIndex - this.index;
        const half = Math.floor(this.slides.length / 2);
        if (distance > half) distance -= this.slides.length;
        if (distance < -half) distance += this.slides.length;
        return distance;
      }

      positionCards() {
        if (!this.slides.length || !this.viewport) return;
        const cardWidth = this.slides[0].offsetWidth;
        const viewportWidth = this.viewport.clientWidth;
        const computedGap = window.getComputedStyle?.(this).getPropertyValue('--jamila-review-gap');
        const gap = Number.parseFloat(computedGap || this.dataset.jamilaGap || '18');
        const center = (viewportWidth - cardWidth) / 2;
        this.slides.forEach((slide, slideIndex) => {
          const distance = this.getRelativeIndex(slideIndex);
          slide.style.setProperty('--jamila-review-x', `${Math.round(center + distance * (cardWidth + gap))}px`);
        });
      }

      show(index, announce = true) {
        if (!this.slides.length) return;
        this.index = (index + this.slides.length) % this.slides.length;
        this.slides.forEach((slide, slideIndex) => {
          const distance = Math.abs(this.getRelativeIndex(slideIndex));
          const active = distance === 0;
          slide.classList.remove('is-active');
          slide.classList.remove('is-near');
          slide.classList.remove('is-far');
          slide.classList.add(active ? 'is-active' : distance === 1 ? 'is-near' : 'is-far');
          slide.setAttribute('aria-hidden', String(!active));
          slide.tabIndex = -1;
          slide.querySelectorAll('[data-jamila-review-source]').forEach((link) => { link.tabIndex = active ? 0 : -1; });
        });
        this.dots.forEach((dot, dotIndex) => {
          const active = dotIndex === this.index;
          dot.setAttribute('aria-pressed', String(active));
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.positionCards();
        if (announce && this.status) this.status.textContent = `Review ${this.index + 1} of ${this.slides.length}`;
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.resizeObserver?.disconnect();
        this.classList.remove('is-dragging');
      }
    });
  }

  document.documentElement.classList.add('jamila-motion-ready');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;

  const revealImmediately = (target) => target.classList.add('is-jamila-visible');
  const prepareRevealTarget = (target) => {
    if (target.hasAttribute('data-jamila-reveal-group')) {
      [...target.children].forEach((child, index) => child.style.setProperty('--jamila-reveal-index', index));
    }
    if (reduceMotion.matches || !('IntersectionObserver' in window)) revealImmediately(target);
    else revealObserver.observe(target);
  };
  const initializeReveals = (root = document) => {
    const targets = [];
    if (root instanceof Element && root.matches('[data-jamila-reveal]')) targets.push(root);
    targets.push(...root.querySelectorAll('[data-jamila-reveal]:not([data-jamila-reveal-ready])'));
    targets.forEach((target) => {
      target.setAttribute('data-jamila-reveal-ready', '');
      prepareRevealTarget(target);
    });
  };

  if ('IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealImmediately(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
  }

  initializeReveals();
  document.addEventListener('shopify:section:load', (event) => initializeReveals(event.target));
  reduceMotion.addEventListener?.('change', (event) => {
    if (!event.matches) return;
    revealObserver?.disconnect();
    document.querySelectorAll('[data-jamila-reveal]').forEach(revealImmediately);
  });

  if (!customElements.get('jamila-product-grid')) {
    customElements.define('jamila-product-grid', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        this.querySelector('[data-jamila-load-more]')?.addEventListener('click', (event) => {
          const deferred = [...this.querySelectorAll('template[data-jamila-deferred-product]')];
          if (!deferred.length) return;
          event.preventDefault();
          const batch = Number.parseInt(this.dataset.jamilaBatch || '4', 10);
          deferred.slice(0, batch).forEach((template) => {
            template.replaceWith(template.content.cloneNode(true));
          });
          const shown = this.querySelectorAll('[data-jamila-product]').length;
          this.querySelector('[data-jamila-grid-status]').textContent = `${shown} products shown`;
          if (deferred.length <= batch) event.currentTarget.textContent = 'View All';
        }, { signal: this.controller.signal });
      }
      disconnectedCallback() { this.controller?.abort(); }
    });
  }

  const hydrateSecondaryProductImage = (target) => {
    const link = target.closest?.('.jamila-product-media > a');
    const image = link?.querySelector?.('[data-jamila-secondary-image]');
    if (!image || image.dataset.jamilaHydrated === 'true') return;
    image.dataset.jamilaHydrated = 'true';
    image.addEventListener('load', () => link.classList.add('is-secondary-ready'), { once: true });
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    image.src = image.dataset.src;
  };
  document.addEventListener('pointerover', (event) => hydrateSecondaryProductImage(event.target));
  document.addEventListener('focusin', (event) => hydrateSecondaryProductImage(event.target));

  if (!customElements.get('jamila-faq')) {
    customElements.define('jamila-faq', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };

        this.items = [...this.querySelectorAll('[data-jamila-faq-item]')];
        this.button = this.querySelector('[data-jamila-faq-more]');
        this.buttonLabel = this.querySelector('[data-jamila-faq-more-label]');
        this.status = this.querySelector('[data-jamila-faq-status]');
        this.collapsedCount = Number.parseInt(this.dataset.jamilaCollapsedCount || '5', 10);
        this.extraItems = this.items.slice(this.collapsedCount);
        this.mobileQuery = window.matchMedia('(max-width: 767px)');
        this.expanded = false;

        this.button?.addEventListener('click', () => this.toggleExtraItems(), options);
        this.onViewportChange = () => this.updateForViewport();
        this.mobileQuery.addEventListener?.('change', this.onViewportChange, options);
        this.updateForViewport();
      }

      updateButton() {
        if (!this.button) return;
        this.button.setAttribute('aria-expanded', String(this.expanded));
        if (this.buttonLabel) this.buttonLabel.textContent = this.expanded ? 'Show Less' : 'Show More';
      }

      updateForViewport() {
        const isMobile = this.mobileQuery.matches;
        this.classList[isMobile ? 'add' : 'remove']('is-faq-mobile');
        if (!this.button || !this.extraItems.length) return;

        this.expanded = false;
        this.items.forEach((item, index) => {
          item.hidden = isMobile && index >= this.collapsedCount;
          item.classList.remove('is-faq-revealing');
          if (isMobile) item.open = false;
        });
        this.button.hidden = !isMobile;
        this.updateButton();
      }

      toggleExtraItems() {
        if (!this.mobileQuery.matches || !this.extraItems.length) return;
        this.expanded = !this.expanded;
        this.extraItems.forEach((item, index) => {
          item.hidden = !this.expanded;
          item.open = false;
          item.classList.remove('is-faq-revealing');
          if (this.expanded) {
            item.style.setProperty('--jamila-faq-show-index', String(index));
            item.classList.add('is-faq-revealing');
          }
        });
        this.updateButton();
        if (this.status) {
          this.status.textContent = this.expanded
            ? `All ${this.items.length} FAQ questions shown`
            : `${Math.min(this.collapsedCount, this.items.length)} FAQ questions shown`;
        }
      }

      disconnectedCallback() { this.controller?.abort(); }
    });
  }

  document.addEventListener('click', (event) => {
    const dismiss = event.target.closest('[data-jamila-dismiss]');
    if (dismiss) dismiss.closest('[data-jamila-announcement]').hidden = true;
  });
})();
